// frontend/src/pages/ExcelTemplateImport.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Container,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { excelImportAPI } from '../services/api';

type ImportStatus = 'uploaded' | 'processing' | 'preview' | 'failed';

interface ImportProgress {
  session_id: string;
  status: ImportStatus;
  is_complete: boolean;
  error_message?: string | null;
  meta?: any;
  template_preview?: any;
  created_template_id?: number | null;
}

const ExcelTemplateImport: React.FC = () => {
  const navigate = useNavigate();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isCreatingDraft, setIsCreatingDraft] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requiresSheetSelection, setRequiresSheetSelection] = useState(false);
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [candidateSheetNames, setCandidateSheetNames] = useState<string[]>([]);
  const [selectedSheetName, setSelectedSheetName] = useState<string>('');

  const canStart = useMemo(() => !!selectedFile && !isUploading, [selectedFile, isUploading]);
  const canCreateDraft = useMemo(() => progress?.status === 'preview' && !isCreatingDraft, [progress, isCreatingDraft]);

  const totalQuestions = progress?.template_preview?.total_questions ?? 0;
  const totalSections = (progress?.template_preview?.structure ?? []).length;
  const confidence = progress?.meta?.confidence;
  const sourceSheet = progress?.meta?.source_sheet;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
    setError(null);
    setSessionId(null);
    setProgress(null);
    setRequiresSheetSelection(false);
    setSheetNames([]);
    setCandidateSheetNames([]);
    setSelectedSheetName('');
  };

  const handleStartImport = async () => {
    if (!selectedFile) return;

    try {
      setIsUploading(true);
      setError(null);

      const data = await excelImportAPI.startImport(selectedFile, selectedSheetName || undefined);
      setSessionId(data.session_id);
    } catch (err: any) {
      console.error('[ERROR] Excel import start failed:', err);
      const resp = err.response?.data;
      if (err.response?.status === 409 && resp?.requires_sheet_selection) {
        setRequiresSheetSelection(true);
        setSheetNames(resp.sheet_names || []);
        setCandidateSheetNames(resp.candidate_sheet_names || []);
        setError(resp.error || 'Please select exactly one sheet to import.');
        // Default to the first candidate if provided (user can change)
        const initial = (resp.candidate_sheet_names && resp.candidate_sheet_names[0]) || (resp.sheet_names && resp.sheet_names[0]) || '';
        setSelectedSheetName(initial);
      } else {
        setError(resp?.error || err.message || 'Failed to start import');
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleCreateDraft = async () => {
    if (!sessionId) return;

    try {
      setIsCreatingDraft(true);
      setError(null);

      const data = await excelImportAPI.createDraft(sessionId);
      const templateId = data.template_id;

      // Reuse the existing AI Builder "resume" flow.
      navigate(`/ai-builder?resume=${templateId}`);
    } catch (err: any) {
      console.error('[ERROR] Create draft failed:', err);
      setError(err.response?.data?.error || err.message || 'Failed to create draft template');
    } finally {
      setIsCreatingDraft(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    let interval: number | null = null;

    const poll = async () => {
      if (!sessionId) return;
      try {
        const data = await excelImportAPI.getProgress(sessionId);
        if (!isMounted) return;
        setProgress(data);
        if (data.status === 'failed' && data.error_message) {
          setError(data.error_message);
        }
      } catch (err: any) {
        if (!isMounted) return;
        console.error('[ERROR] Poll import progress failed:', err);
        setError(err.response?.data?.error || err.message || 'Failed to load import progress');
      }
    };

    if (sessionId) {
      poll();
      interval = window.setInterval(poll, 2000);
    }

    return () => {
      isMounted = false;
      if (interval) window.clearInterval(interval);
    };
  }, [sessionId]);

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Template Import (Excel)
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Upload an existing Excel template and let AI map it into a draft review template.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Box>
            <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
              Choose file (.xlsx / .xlsm)
              <input hidden type="file" accept=".xlsx,.xlsm" onChange={handleFileChange} />
            </Button>
            {selectedFile && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Selected: {selectedFile.name}
              </Typography>
            )}
          </Box>

          <Box>
            <Button
              variant="contained"
              onClick={handleStartImport}
              disabled={!canStart}
              startIcon={<AutoFixHighIcon />}
            >
              Start AI Import
            </Button>
          </Box>

          {isUploading && <LinearProgress />}

          {requiresSheetSelection && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Select one sheet to import
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel id="excel-import-sheet-label">Sheet</InputLabel>
                <Select
                  labelId="excel-import-sheet-label"
                  label="Sheet"
                  value={selectedSheetName}
                  onChange={(e) => setSelectedSheetName(e.target.value as string)}
                >
                  {(candidateSheetNames.length > 0 ? candidateSheetNames : sheetNames).map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary">
                We found multiple possible template sheets. Please choose one and click “Start AI Import” again.
              </Typography>
            </Box>
          )}

          {sessionId && (
            <>
              <Divider />
              <Box>
                <Typography variant="h6" gutterBottom>
                  Import status
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Session: {sessionId}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Status: <strong>{progress?.status || 'processing'}</strong>
                </Typography>
                {progress?.status === 'processing' && (
                  <Box sx={{ mt: 2 }}>
                    <LinearProgress />
                    <Typography variant="caption" color="text.secondary">
                      Analysing your spreadsheet and mapping questions/options…
                    </Typography>
                  </Box>
                )}
              </Box>

              {progress?.status === 'preview' && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Preview
                    </Typography>
                    <Stack spacing={1}>
                      <Typography variant="body2">
                        Sections: <strong>{totalSections}</strong>
                      </Typography>
                      <Typography variant="body2">
                        Questions: <strong>{totalQuestions}</strong>
                      </Typography>
                      {typeof confidence === 'number' && (
                        <Typography variant="body2">
                          Confidence: <strong>{Math.round(confidence * 100)}%</strong>
                        </Typography>
                      )}
                      {sourceSheet && (
                        <Typography variant="body2">
                          Source sheet: <strong>{sourceSheet}</strong>
                        </Typography>
                      )}
                    </Stack>
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        onClick={handleCreateDraft}
                        disabled={!canCreateDraft}
                        startIcon={<OpenInNewIcon />}
                      >
                        Create draft and open in builder
                      </Button>
                      {isCreatingDraft && <LinearProgress sx={{ mt: 2 }} />}
                    </Box>
                  </Box>
                </>
              )}
            </>
          )}
        </Stack>
      </Paper>
    </Container>
  );
};

export default ExcelTemplateImport;


